import getValidFolderPaneWidth from './helpers/getValidFolderPaneWidth';
import { initializeDynamicLayout } from '../actions/initializeDynamicLayout';
import { onClientReadingPanePositionChange } from '../actions/onClientReadingPanePositionChange';
import logFolderPaneState from '../helpers/logFolderPaneState';
import { default as setFolderPaneWidth } from '../legacyActions/setFolderPaneWidth';
import setCurrentClientReadingPanePosition from '../mutators/setCurrentClientReadingPanePositionMutator';
import setShowFolderPane from '../mutators/setShowFolderPaneMutator';
import { setShowListPane } from '../actions/setShowListPane';
import { setShowReadingPane } from '../actions/setShowReadingPane';
import LayoutChangeSource from '../store/schema/LayoutChangeSource';
import { getIsBitSet, ListViewBitFlagsMasks } from 'owa-bit-flags/lib/utils/listViewBitFlagsUtil';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';
import { orchestrator } from 'satcheljs';
import {
    isReadingPanePositionRight,
    isReadingPanePositionOff,
} from '../selectors/readingPanePosition';

/**
 * This orchestrator is responsible for listening to the media query changes for the max-width breakpoints
 * and take appropriate actions related to layout
 */
export default orchestrator(initializeDynamicLayout, actionMessage => {
    /**
     * On init try to validate the folder pane width (NavigationBarWidth)
     */
    let storedFolderPaneWidth = getUserConfiguration().UserOptions.NavigationBarWidth;
    storedFolderPaneWidth = getValidFolderPaneWidth(storedFolderPaneWidth, LayoutChangeSource.Init);

    // Set folder pane width to a valid width
    setFolderPaneWidth(storedFolderPaneWidth);

    /**
     * First breakpoint is to collapse/expand left nav by showing/hiding folder pane
     * Second breakpoint is to show/hide reading pane/list view
     */
    const mediaQueryForFirstBreakPoint = window.matchMedia('(max-width: 770px)');
    const mediaQueryForSecondBreakPoint = window.matchMedia('(max-width: 658px)');

    mediaQueryForFirstBreakPoint.addListener(onFirstBreakPointHit(LayoutChangeSource.WindowResize));
    mediaQueryForSecondBreakPoint.addListener(
        onSecondBreakPointHit(LayoutChangeSource.WindowResize)
    );

    // Call match query on boot because the current browser state might in one of these states
    onFirstBreakPointHit(LayoutChangeSource.Init)(mediaQueryForFirstBreakPoint);
    onSecondBreakPointHit(LayoutChangeSource.Init)(mediaQueryForSecondBreakPoint);
});

/**
 * Handler for when the window size breaks at 770px
 * Start collapsing/expanding left nav
 */
const onFirstBreakPointHit = (source: LayoutChangeSource) => mediaQueryListEvent => {
    if (mediaQueryListEvent.matches) {
        // Hide FP when user's window size is <= 1st breakpoint
        setShowFolderPane(false);
    } else {
        // Show/hide FP as per user setting if user's window size is > 1st breakpoint
        const showFolderPane = !getIsBitSet(ListViewBitFlagsMasks.FolderPaneCollapsed);
        setShowFolderPane(showFolderPane);
        logFolderPaneState(source, showFolderPane, showFolderPane);
    }
};

/**
 * Handler for when the window size breaks at 658px
 * Start showing/hiding reading pane or list view for when reading pane position is right.
 * Currently we are changing the RP layout only for RP position right
 */
const onSecondBreakPointHit = (source: LayoutChangeSource) => mediaQueryListEvent => {
    const currentReadingPanePositionRight = isReadingPanePositionRight();
    if (mediaQueryListEvent.matches) {
        // Change RP position to Off when user's window size is <= 2nd breakpoint
        if (currentReadingPanePositionRight) {
            setCurrentClientReadingPanePosition(ReadingPanePosition.Off);
            setShowListPane(true /* showListPane */);
            setShowReadingPane(false);
            onClientReadingPanePositionChange();
        }
    } else {
        // Change RP position as per user's setting when user's window size is > 2nd breakpoint
        setCurrentClientReadingPanePosition(null);
        setShowListPane(true /* showListPane */);
        setShowReadingPane(!isReadingPanePositionOff());
        onClientReadingPanePositionChange();
    }
};
